import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { alpha, useTheme } from '@mui/material/styles';

import type { DeviceRecord } from '@/data/types';

interface RetiredDeviceDetailProps {
  device: DeviceRecord;
  clientName: string;
}

/**
 * RetiredDeviceDetail — displays retirement information for a retired device
 * and provides a Copy Details button for creating remediation tickets.
 */
export default function RetiredDeviceDetail({ device, clientName }: RetiredDeviceDetailProps) {
  const [copied, setCopied] = useState(false);
  const theme = useTheme();

  const retiredDateFormatted = device.retiredDate
    ? new Date(device.retiredDate).toLocaleDateString()
    : 'Unknown';

  const retireReason = device.retireComment || 'No reason provided';
  const location = device.locationName || 'Unknown';

  const activeAgents = Object.entries(device.platforms)
    .filter(([, present]) => present)
    .map(([name]) => name);

  function copyRetiredDetails() {
    const agentLines =
      activeAgents.length > 0 ? activeAgents.map((a) => `- ${a}`).join('\n') : '- None';

    const text = [
      `Device: ${device.deviceName}`,
      `Client: ${clientName}`,
      `Location: ${location}`,
      `Retired: ${retiredDateFormatted}`,
      `Reason: ${retireReason}`,
      '',
      'Active Agents to Remove:',
      agentLines,
    ].join('\n');

    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        console.warn('[RetiredDeviceDetail] Clipboard API unavailable');
      });
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        bgcolor: alpha(theme.palette.warning.main, 0.04),
        borderColor: alpha(theme.palette.warning.main, 0.2),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'warning.main' }}>
          Retirement Details
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
          onClick={copyRetiredDetails}
          sx={{
            fontSize: '0.7rem',
            textTransform: 'none',
            borderColor: copied
              ? alpha(theme.palette.success.main, 0.5)
              : alpha(theme.palette.divider, 1),
            color: copied ? 'success.main' : 'text.secondary',
          }}
        >
          {copied ? 'Copied!' : 'Copy Details'}
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 0.75, rowGap: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Retired Date:
        </Typography>
        <Typography variant="caption">{retiredDateFormatted}</Typography>

        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Reason:
        </Typography>
        <Typography variant="caption">{retireReason}</Typography>

        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Location:
        </Typography>
        <Typography variant="caption">{location}</Typography>

        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Active Agents:
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {activeAgents.length > 0 ? (
            activeAgents.map((agent) => (
              <Chip
                key={agent}
                label={agent}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  fontFamily: 'var(--font-mono)',
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  color: 'error.main',
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                }}
              />
            ))
          ) : (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              None
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
